import { useState, useEffect } from 'react';
import { hasPermission } from '../../../lib/permissions';
import apiClient from '../../../lib/api';
import { resolveImageUrl } from '../../../lib/utils';

interface ContactPageSettings {
  hero_title: string;
  hero_subtitle: string;
  hero_image_url: string;
}

interface ContactInfo {
  hero: {
    title: string;
    subtitle: string;
  };
  quickContact: {
    phone: string;
    email: string;
    whatsapp: string;
    address: string;
    weekdays_hours: string;
    saturday_hours: string;
    sunday_hours: string;
  };
  socialMedia: {
    facebook_url: string;
    instagram_url: string;
    twitter_url: string;
    youtube_url: string;
    linkedin_url: string;
  };
  locations: Location[];
}

interface Location {
  id: string;
  name: string;
  address: string;
  phone: string;
  email?: string;
  hours?: string;
  map_url: string;
  is_active: number;
  order_index?: number;
}

const ContactManager = () => {
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    hero: {
      title: 'Get in Touch',
      subtitle: 'Ready to experience premium grooming? Contact us today!'
    },
    quickContact: {
      phone: '',
      email: '',
      whatsapp: '',
      address: '',
      weekdays_hours: '',
      saturday_hours: '',
      sunday_hours: ''
    },
    socialMedia: {
      facebook_url: '',
      instagram_url: '',
      twitter_url: '',
      youtube_url: '',
      linkedin_url: ''
    },
    locations: []
  });
  const [contactPageSettings, setContactPageSettings] = useState<ContactPageSettings>({
    hero_title: 'Get in Touch',
    hero_subtitle: 'Ready to experience premium grooming? Contact us today!',
    hero_image_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [imageFiles, setImageFiles] = useState<{ hero: File | null }>({ hero: null });
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  useEffect(() => {
    loadContactData();
  }, []);


  const loadContactData = async () => {
    try {
      setLoading(true);

      let contactData = {};
      let locationsData = [];
      let pageSettings = {};

      try {
        contactData = await apiClient.getContactInfo();
      } catch (e) {
        console.error('Failed to load contact info', e);
      }

      try {
        locationsData = await apiClient.getContactLocations();
      } catch (e) {
        console.error('Failed to load locations', e);
      }

      try {
        pageSettings = await apiClient.getContactPageSettings();
      } catch (e) {
        console.error('Failed to load page settings', e);
      }

      setContactInfo({
        hero: {
          title: (pageSettings as any).hero_title || 'Get in Touch',
          subtitle: (pageSettings as any).hero_subtitle || 'Ready to experience premium grooming? Contact us today!'
        },
        quickContact: {
          phone: (contactData as any).phone || '',
          email: (contactData as any).email || '',
          whatsapp: (contactData as any).whatsapp || '',
          address: (contactData as any).address || '',
          weekdays_hours: (contactData as any).weekdays_hours || '',
          saturday_hours: (contactData as any).saturday_hours || '',
          sunday_hours: (contactData as any).sunday_hours || ''
        },
        socialMedia: {
          facebook_url: (contactData as any).facebook_url || '',
          instagram_url: (contactData as any).instagram_url || '',
          twitter_url: (contactData as any).twitter_url || '',
          youtube_url: (contactData as any).youtube_url || '',
          linkedin_url: (contactData as any).linkedin_url || ''
        },
        locations: locationsData || []
      });

      setContactPageSettings({
        hero_title: (pageSettings as any).hero_title || 'Get in Touch',
        hero_subtitle: (pageSettings as any).hero_subtitle || 'Ready to experience premium grooming? Contact us today!',
        hero_image_url: (pageSettings as any).hero_image_url || ''
      });

    } catch (error) {
      console.error('Failed to load contact data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);

      // Handle file uploads first
      let updatedSettings = { ...contactPageSettings };

      // Upload hero image if selected
      // Upload hero image if selected
      if (imageFiles.hero) {
        const formData = new FormData();
        formData.append('image', imageFiles.hero);
        try {
          console.log('[DEBUG] Starting Hero Image Upload...');
          const uploadResult = await apiClient.uploadImage(formData, 'contact');
          console.log('[DEBUG] Hero Upload Result:', uploadResult);

          if (uploadResult && uploadResult.imageUrl) {
            updatedSettings.hero_image_url = uploadResult.imageUrl;
            console.log('[DEBUG] Updated hero_image_url:', updatedSettings.hero_image_url);
          } else {
            console.error('[DEBUG] Upload succeeded but NO imageUrl returned!');
          }
        } catch (error: any) {
          console.error('Failed to upload hero image:', error);
          alert(`Failed to upload hero image: ${error?.message || 'Unknown error'}`);
          setSaving(false);
          return;
        }
      }

      // Save contact page settings
      console.log('[DEBUG] Sending updateContactPageSettings with:', updatedSettings);
      const savedSettings = await apiClient.updateContactPageSettings(updatedSettings);
      console.log('[DEBUG] Server responded with:', savedSettings);

      // Clear uploaded files and show success
      setImageFiles({ hero: null });
      alert('Contact page settings saved successfully!');

      // Reload data to reflect changes
      await loadContactData();

    } catch (error: any) {
      console.error('Failed to save settings:', error);
      alert(`Failed to save settings: ${error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveContactInfo = async (section: string, data: any) => {
    setContactInfo(prev => ({
      ...prev,
      [section]: data
    }));

    if (section === 'quickContact' || section === 'socialMedia') {
      try {
        const updatedInfo = { ...contactInfo, [section]: data };
        const apiData = {
          phone: updatedInfo.quickContact.phone,
          email: updatedInfo.quickContact.email,
          whatsapp: updatedInfo.quickContact.whatsapp,
          address: updatedInfo.quickContact.address,
          weekdays_hours: updatedInfo.quickContact.weekdays_hours,
          saturday_hours: updatedInfo.quickContact.saturday_hours,
          sunday_hours: updatedInfo.quickContact.sunday_hours,
          facebook_url: updatedInfo.socialMedia.facebook_url,
          instagram_url: updatedInfo.socialMedia.instagram_url,
          twitter_url: updatedInfo.socialMedia.twitter_url,
          youtube_url: updatedInfo.socialMedia.youtube_url,
          linkedin_url: updatedInfo.socialMedia.linkedin_url
        };
        await apiClient.updateContactInfo(apiData);
      } catch (error) {
        console.error('Failed to save contact info:', error);
      }
    }
  };

  const handleEditLocation = (location: Location) => {
    setEditingLocation({ ...location });
    setShowLocationModal(true);
  };

  const handleAddLocation = () => {
    const newLocation: Location = {
      id: '',
      name: '',
      address: '',
      phone: '',
      email: '',
      hours: '',
      map_url: '',
      is_active: 1,
      order_index: 0
    };
    setEditingLocation(newLocation);
    setShowLocationModal(true);
  };

  const handleSaveLocation = async () => {
    if (!editingLocation) return;

    try {
      if (editingLocation.id && contactInfo.locations.find(l => l.id === editingLocation.id)) {
        await apiClient.updateContactLocation(editingLocation.id, editingLocation);
      } else {
        await apiClient.createContactLocation(editingLocation);
      }
      await loadContactData();
      setShowLocationModal(false);
      setEditingLocation(null);
    } catch (error) {
      console.error('Failed to save location:', error);
    }
  };

  const handleDeleteLocation = async (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();

    console.log('[ContactManager] handleDeleteLocation clicked for id:', id);

    if (deleteConfirmId === id) {
      // Confirmed, proceed to delete
      try {
        console.log('[ContactManager] Confirmed deletion, calling API for:', id);
        await apiClient.deleteContactLocation(id);
        console.log('[ContactManager] Delete successful');
        setDeleteConfirmId(null); // Reset
        await loadContactData();
      } catch (error: any) {
        console.error('[ContactManager] Failed to delete location:', error);
        alert(`Failed to delete: ${error.message}`);
      }
    } else {
      // First click, set confirm state
      console.log('[ContactManager] First click, setting confirmation state for:', id);
      setDeleteConfirmId(id);
      // Auto-reset after 3 seconds if not clicked
      setTimeout(() => {
        console.log('[ContactManager] Auto-resetting confirmation state for:', id);
        setDeleteConfirmId(null);
      }, 3000);
    }
  };

  const toggleLocationStatus = async (id: string) => {
    const location = contactInfo.locations.find(l => l.id === id);
    if (location) {
      try {
        const updatedLocation = { ...location, is_active: location.is_active ? 0 : 1 };
        await apiClient.updateContactLocation(id, updatedLocation);
        await loadContactData();
      } catch (error) {
        console.error('Failed to toggle location status:', error);
      }
    }
  };

  // Use the exact permission IDs that exist in adminPermissions storage
  const sections = [
    { id: 'hero', name: 'Hero Section', icon: 'ri-home-4-line', permKey: 'contact-hero' },
    { id: 'contact', name: 'Contact Info', icon: 'ri-phone-line', permKey: 'contact-info' },
    { id: 'social', name: 'Social Media', icon: 'ri-share-line', permKey: 'contact-social' },
    { id: 'locations', name: 'Locations', icon: 'ri-map-pin-line', permKey: 'contact-locations' }
  ];

  /**
   * Small helper to check permissions for a UI section.
   * It checks the explicit permKey (from adminPermissions), and falls back to other forms
   * (section id or whole 'contact' permission) to remain compatible with older setups.
   */
  const hasPermFor = (sectionId: string, permKey?: string) => {
    // first, if explicit permKey passed, check it
    if (permKey) {
      if (hasPermission(permKey)) return true;
      // if explicit permKey exists but disabled, return false
      // (we don't fall back immediately; this respects master setting)
      return false;
    }

    // fallback checks for compatibility (rare)
    if (sectionId === 'contact') {
      return (
        hasPermission('contact-info') ||
        hasPermission('contact-contact') ||
        hasPermission('contact.quick') ||
        hasPermission('contact') ||
        hasPermission('quick-contact') ||
        hasPermission('quick')
      );
    }

    return (
      hasPermission(`contact-${sectionId}`) ||
      hasPermission(`contact.${sectionId}`) ||
      hasPermission(sectionId) ||
      hasPermission('contact')
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading contact information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-900">Contact Page Management</h3>
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="bg-primary-400 hover:bg-primary-500 text-black px-6 py-3 rounded-lg font-semibold transition-all duration-300 disabled:opacity-50 flex items-center mr-4"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <i className="ri-save-line mr-2"></i>
              Save Settings
            </>
          )}
        </button>
      </div>
      <div className="flex space-x-2 flex-wrap">
        {sections.map((section) => (
          // check explicit permKey first (the items in storage use these IDs)
          hasPermFor(section.id, section.permKey) ? (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`px-3 py-2 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer text-sm ${activeSection === section.id
                ? 'bg-primary-400 text-black'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              <i className={`${section.icon} mr-1`}></i>
              {section.name}
            </button>
          ) : null
        ))}
      </div>

      {/* Hero Section */}
      {activeSection === 'hero' && hasPermFor('hero', 'contact-hero') && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h4 className="text-lg font-bold text-gray-900 mb-4">Hero Section</h4>
          <div className="space-y-4">

            <div>
              <label className="block text-gray-700 font-semibold mb-2">Title</label>
              <input
                type="text"
                value={contactPageSettings.hero_title}
                onChange={(e) => setContactPageSettings({ ...contactPageSettings, hero_title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Subtitle</label>
              <textarea
                value={contactPageSettings.hero_subtitle}
                onChange={(e) => setContactPageSettings({ ...contactPageSettings, hero_subtitle: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                rows={2}
              />
            </div>
          </div>
        </div>
      )}

      {/* Contact Information Section */}
      {activeSection === 'contact' && hasPermFor('contact', 'contact-info') && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h4 className="text-lg font-bold text-gray-900 mb-4">Contact Information</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Phone Number</label>
              <input
                type="tel"
                value={contactInfo.quickContact.phone}
                onChange={(e) => handleSaveContactInfo('quickContact', { ...contactInfo.quickContact, phone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Email Address</label>
              <input
                type="email"
                value={contactInfo.quickContact.email}
                onChange={(e) => handleSaveContactInfo('quickContact', { ...contactInfo.quickContact, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">WhatsApp Number</label>
              <input
                type="tel"
                value={contactInfo.quickContact.whatsapp}
                onChange={(e) => handleSaveContactInfo('quickContact', { ...contactInfo.quickContact, whatsapp: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Address</label>
              <textarea
                value={contactInfo.quickContact.address}
                onChange={(e) => handleSaveContactInfo('quickContact', { ...contactInfo.quickContact, address: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Weekdays Hours</label>
                <input
                  type="text"
                  value={contactInfo.quickContact.weekdays_hours}
                  onChange={(e) => handleSaveContactInfo('quickContact', { ...contactInfo.quickContact, weekdays_hours: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="9:00 AM - 9:00 PM"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Saturday Hours</label>
                <input
                  type="text"
                  value={contactInfo.quickContact.saturday_hours}
                  onChange={(e) => handleSaveContactInfo('quickContact', { ...contactInfo.quickContact, saturday_hours: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="9:00 AM - 10:00 PM"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Sunday Hours</label>
                <input
                  type="text"
                  value={contactInfo.quickContact.sunday_hours}
                  onChange={(e) => handleSaveContactInfo('quickContact', { ...contactInfo.quickContact, sunday_hours: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="10:00 AM - 8:00 PM"
                />
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Social Media Section */}
      {activeSection === 'social' && hasPermFor('social', 'contact-social') && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h4 className="text-lg font-bold text-gray-900 mb-4">Social Media Links</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Facebook URL</label>
              <input
                type="url"
                value={contactInfo.socialMedia.facebook_url}
                onChange={(e) => handleSaveContactInfo('socialMedia', { ...contactInfo.socialMedia, facebook_url: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                placeholder="https://facebook.com/yourpage"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Instagram URL</label>
              <input
                type="url"
                value={contactInfo.socialMedia.instagram_url}
                onChange={(e) => handleSaveContactInfo('socialMedia', { ...contactInfo.socialMedia, instagram_url: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                placeholder="https://instagram.com/yourpage"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Twitter URL</label>
              <input
                type="url"
                value={contactInfo.socialMedia.twitter_url}
                onChange={(e) => handleSaveContactInfo('socialMedia', { ...contactInfo.socialMedia, twitter_url: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                placeholder="https://twitter.com/yourpage"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">YouTube URL</label>
              <input
                type="url"
                value={contactInfo.socialMedia.youtube_url}
                onChange={(e) => handleSaveContactInfo('socialMedia', { ...contactInfo.socialMedia, youtube_url: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                placeholder="https://youtube.com/@yourchannel"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">LinkedIn URL</label>
              <input
                type="url"
                value={contactInfo.socialMedia.linkedin_url}
                onChange={(e) => handleSaveContactInfo('socialMedia', { ...contactInfo.socialMedia, linkedin_url: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                placeholder="https://linkedin.com/company/yourcompany"
              />
            </div>
          </div>
        </div>
      )}

      {/* Locations Section */}
      {activeSection === 'locations' && hasPermFor('locations', 'contact-locations') && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-bold text-gray-900">Our Locations</h4>
            <button
              onClick={handleAddLocation}
              className="bg-primary-400 hover:bg-primary-500 text-black px-4 py-2 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center"
            >
              <i className="ri-add-line mr-2"></i>
              Add Location
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {contactInfo.locations.map((location) => (
              <div key={location.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h5 className="font-semibold text-gray-900 mb-2">{location.name}</h5>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p><i className="ri-map-pin-line mr-2"></i>{location.address}</p>
                      <p><i className="ri-phone-line mr-2"></i>{location.phone}</p>
                    </div>
                    <div className="mt-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${location.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {location.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => toggleLocationStatus(location.id)}
                      className="text-gray-600 hover:text-gray-800 cursor-pointer"
                    >
                      <i className={location.is_active ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
                    </button>
                    <button
                      onClick={() => handleEditLocation(location)}
                      className="text-blue-600 hover:text-blue-800 cursor-pointer"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDeleteLocation(e, location.id)}
                      className={`${deleteConfirmId === location.id ? 'bg-red-600 text-white px-2 py-1 rounded text-xs' : 'text-red-600 hover:text-red-800'} cursor-pointer transition-all`}
                    >
                      {deleteConfirmId === location.id ? 'Confirm?' : <i className="ri-delete-bin-line"></i>}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Location Modal */}
      {showLocationModal && editingLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {contactInfo.locations.find(l => l.id === editingLocation.id) ? 'Edit Location' : 'Add Location'}
              </h3>
              <button
                onClick={() => setShowLocationModal(false)}
                className="text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Branch Name</label>
                <input
                  type="text"
                  value={editingLocation.name}
                  onChange={(e) => setEditingLocation({ ...editingLocation, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="Andheri Branch"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Address</label>
                <textarea
                  value={editingLocation.address}
                  onChange={(e) => setEditingLocation({ ...editingLocation, address: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  rows={3}
                  placeholder="123 Street Name, Area, City - PIN Code"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={editingLocation.phone}
                  onChange={(e) => setEditingLocation({ ...editingLocation, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="+91 7484984048"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Email Address</label>
                <input
                  type="email"
                  value={editingLocation.email || ''}
                  onChange={(e) => setEditingLocation({ ...editingLocation, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="branch@salon.com"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Operating Hours</label>
                <input
                  type="text"
                  value={editingLocation.hours || ''}
                  onChange={(e) => setEditingLocation({ ...editingLocation, hours: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="Mon-Sun: 10:00 AM - 9:00 PM"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Google Maps Embed URL</label>
                <textarea
                  value={editingLocation.map_url}
                  onChange={(e) => setEditingLocation({ ...editingLocation, map_url: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  rows={3}
                  placeholder="https://www.google.com/maps/embed?..."
                />
                <p className="text-sm text-gray-600 mt-1">
                  Go to Google Maps, search for your location, click Share → Embed, and copy the URL
                </p>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Display Order</label>
                <input
                  type="number"
                  value={editingLocation.order_index || 0}
                  onChange={(e) => setEditingLocation({ ...editingLocation, order_index: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="0"
                  min="0"
                />
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="locationActive"
                  checked={editingLocation.is_active === 1}
                  onChange={(e) => setEditingLocation({ ...editingLocation, is_active: e.target.checked ? 1 : 0 })}
                  className="w-4 h-4 text-primary-400 border-gray-300 rounded focus:ring-primary-400"
                />
                <label htmlFor="locationActive" className="text-gray-700 font-semibold">
                  Active
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowLocationModal(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLocation}
                className="px-6 py-2 bg-primary-400 hover:bg-primary-500 text-black rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Save Location
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactManager;
